import React, { useState, useEffect } from 'react';
import { Plus, X, Check, CreditCard, ToggleLeft, ToggleRight } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';

const Subscriptions = () => {
  const [subs, setSubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const f = searchParams.get('filter');
    if (f) setFilter(f);
  }, [searchParams]);
  const [form, setForm] = useState({ user_id: '', mac_address: '', package_id: '', months: 1 });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [s, u, p] = await Promise.all([
        api.get('/api/subscriptions'),
        api.get('/api/users'),
        api.get('/api/packages')
      ]);
      setSubs(s.data.data || []);
      setUsers((u.data.data || []).filter(u => u.role === 'enduser'));
      setPackages(p.data.data || []);
    } catch (err) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/subscriptions', form);
      toast.success('Subscription activated');
      setShowModal(false);
      setForm({ user_id: '', mac_address: '', package_id: '', months: 1 });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleToggle = async (id) => {
    try { await api.put(`/api/subscriptions/${id}/toggle`); toast.success('Updated'); fetchAll(); }
    catch (err) { toast.error('Failed'); }
  };
  const handleRenew = async (pkg, mac) => {
    try {
      await api.post('/api/subscriptions', { user_id: pkg.user_id, mac_address: mac, package_id: pkg.package_id, months: 1 });
      toast.success('Renewed: ' + (pkg.package_name || 'package'));
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.message || 'Renew failed'); }
  };

  const filtered = subs.filter(s => {
    if (filter === 'active') return s.is_active && new Date(s.end_date) > new Date();
    if (filter === 'expired') return !s.is_active || new Date(s.end_date) <= new Date();
    return true;
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Subscriptions</div>
          <div className="page-subtitle">{subs.length} total</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} /> Activate
        </button>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {['all','active','expired'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className="btn" style={{
            padding: '8px 16px', fontSize: '13px', textTransform: 'capitalize',
            background: filter === f ? 'var(--accent-blue)' : 'var(--bg-card)',
            color: filter === f ? 'white' : 'var(--text-secondary)',
            border: '1px solid var(--border)'
          }}>{f}</button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? <div className="loading"><div className="spinner" />Loading...</div> : (
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>MAC Address</th>
                <th>Package</th>
                <th>Price Paid</th>
                <th>Start</th>
                <th>Expiry</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const now = new Date();
                const allGrouped = {};
                subs.forEach(s => {
                  const mac = s.mac_address || 'unknown';
                  if (!allGrouped[mac]) allGrouped[mac] = { ...s, packages: [] };
                  allGrouped[mac].packages.push(s);
                });
                const rows = Object.values(allGrouped).map(g => {
                  const activePkgs = g.packages.filter(p => p.is_active && new Date(p.end_date) > now);
                  const expiredPkgs = g.packages
                    .filter(p => !p.is_active || new Date(p.end_date) <= now)
                    .filter(p => !g.packages.some(other => other.package_id === p.package_id && new Date(other.end_date) > new Date(p.end_date)));
                  return { ...g, activePkgs, expiredPkgs };
                }).filter(g => {
                  if (filter === 'active') return g.activePkgs.length > 0;
                  if (filter === 'expired') return g.expiredPkgs.length > 0;
                  return g.activePkgs.length > 0 || g.expiredPkgs.length > 0;
                });
                return rows.map(g => (
                  <tr key={g.mac_address}>
                    <td style={{ fontWeight: '500' }}>{g.full_name || g.username || '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{g.mac_address || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {filter !== 'expired' && g.activePkgs.map(p => (
                          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span className="badge badge-info" style={{ fontSize: '11px' }}>{p.package_name || '—'}</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{p.end_date ? new Date(p.end_date).toLocaleDateString('en-IN') : '—'}</span>
                            <button onClick={() => handleToggle(p.id)} title="Suspend this package" className="btn btn-secondary" style={{ padding: '2px 6px' }}>
                              <ToggleRight size={12} color="var(--accent-teal)" />
                            </button>
                          </div>
                        ))}
                        {filter !== 'active' && g.expiredPkgs.map(p => {
                          const stillTimeValid = new Date(p.end_date) > now;
                          return (
                            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: filter === 'expired' ? 1 : 0.4 }}>
                              <span className="badge badge-danger" style={{ fontSize: '10px' }}>{p.package_name || '—'}</span>
                              <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>{p.end_date ? new Date(p.end_date).toLocaleDateString('en-IN') : '—'} {stillTimeValid ? '(disabled)' : '(exp)'}</span>
                              {stillTimeValid ? (
                                <button onClick={() => handleToggle(p.id)} title="Re-enable this package" className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: '10px' }}>Reactivate</button>
                              ) : (
                                <button onClick={() => handleRenew(p, g.mac_address)} className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: '10px' }}>Renew</button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td>₹{g.packages.reduce((sum, p) => sum + parseFloat(p.price_paid || 0), 0).toFixed(2)}</td>
                    <td style={{ fontSize: '13px' }}>{g.start_date ? new Date(g.start_date).toLocaleDateString('en-IN') : '—'}</td>
                    <td style={{ fontSize: '13px' }}>{g.packages.reduce((latest, p) => new Date(p.end_date) > new Date(latest) ? p.end_date : latest, g.end_date)  ? new Date(g.packages.reduce((latest, p) => new Date(p.end_date) > new Date(latest) ? p.end_date : latest, g.end_date)).toLocaleDateString('en-IN') : '—'}</td>
                    <td>
                      <span className={`badge ${g.activePkgs.length > 0 ? 'badge-success' : 'badge-danger'}`}>
                        {g.activePkgs.length > 0 ? 'Active' : 'Expired'}
                      </span>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Activate Subscription</div>
              <button className="close-btn" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>End User *</label>
                <select className="form-control" value={form.user_id} onChange={e => setForm({...form, user_id: e.target.value})} required>
                  <option value="">Select user...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>MAC Address *</label>
                <input className="form-control" value={form.mac_address} onChange={e => setForm({...form, mac_address: e.target.value})} required placeholder="AA:BB:CC:DD:EE:FF" />
              </div>
              <div className="form-group">
                <label>Package *</label>
                <select className="form-control" value={form.package_id} onChange={e => setForm({...form, package_id: e.target.value})} required>
                  <option value="">Select package...</option>
                  {packages.map(p => <option key={p.id} value={p.id}>{p.name} ({p.channel_count} channels)</option>)}
                </select>
              </div>
              {(() => {
                const selectedPackage = packages.find(p => p.id === form.package_id);
                if (selectedPackage && selectedPackage.validity_days) {
                  return (
                    <div className="form-group">
                      <label>Validity</label>
                      <div style={{ padding: '10px 12px', background: 'rgba(0,180,216,.08)', border: '1px solid rgba(0,180,216,.3)', borderRadius: '6px', fontSize: '13px', color: '#00B4D8' }}>
                        {selectedPackage.validity_label || (selectedPackage.validity_days + ' days')} (set by this package)
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="form-group">
                    <label>Duration (months)</label>
                    <select className="form-control" value={form.months} onChange={e => setForm({...form, months: parseInt(e.target.value)})}>
                      {[1,2,3,6,12].map(m => <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>)}
                    </select>
                  </div>
                );
              })()}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-success"><Check size={16} />Activate</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
